import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Button,
  Checkbox,
  Field,
  Label,
  Link,
  Paragraph,
  Textarea,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { SafetyRepWork } from '../../../api/safetyRepWork';
import {
  useReturnDocumentationMutation,
  useSignDocumentationMutation,
} from '../../../hooks/queries/safetyRep/useSafetyRepWork';
import { surveyPath } from '../../WorkEnvironmentSurveyDetail/surveyRoutes';
import { decisionError, type Decision } from '../safetyRepView';
import layout from '../../HRDashboard/hmsPage.module.css';
import classes from '../SafetyRep.module.css';

type Props = {
  item: SafetyRepWork['documentation'][number];
  onDone: (decision: Decision, comment: string) => void;
};

/**
 * The safety representative reads the documentation, and signs it or says what is missing. Shown under the task in
 * «Mine oppgaver», which has the title.
 */
export const DocumentationDecision = ({ item, onDone }: Props): ReactElement => {
  const { t } = useTranslation();
  const [decision, setDecision] = useState<Decision | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [comment, setComment] = useState('');
  const [tried, setTried] = useState(false);
  const sign = useSignDocumentationMutation();
  const giveBack = useReturnDocumentationMutation();
  const id = `documentation-${item.surveyId}`;
  const invalid =
    tried && decision !== null && decisionError(decision, confirmed, comment);

  const start = (next: Decision | null) => {
    setDecision(next);
    setTried(false);
  };
  const confirm = () => {
    if (!decision) return;
    if (decisionError(decision, confirmed, comment)) {
      setTried(true);
      return;
    }
    const text = comment.trim();
    const done = { onSuccess: () => onDone(decision, text) };
    if (decision === 'approve') sign.mutate(item.surveyId, done);
    else giveBack.mutate({ surveyId: item.surveyId, comment: text }, done);
  };

  return (
    <div className={classes.decisionPanel}>
      <Paragraph data-size="sm" className={classes.text}>
        {t('safetyRep.work.documentation.text')}
      </Paragraph>
      <Link asChild data-size="sm" className={layout.startAligned}>
        <RouterLink to={surveyPath('safetyRep', item.surveyId)}>
          {t('safetyRep.work.documentation.read')}
        </RouterLink>
      </Link>
      {decision === null && (
        <div className={layout.actions} data-gap="sm">
          <Button
            variant="primary"
            data-size="sm"
            onClick={() => start('approve')}
          >
            {t('safetyRep.work.documentation.sign')}
          </Button>
          <Button
            variant="secondary"
            data-size="sm"
            onClick={() => start('return')}
          >
            {t('safetyRep.signing.report')}
          </Button>
        </div>
      )}
      {decision === 'approve' && (
        <div className={classes.decision}>
          <Checkbox
            data-size="sm"
            id={`${id}-confirm`}
            label={t('safetyRep.work.documentation.confirm')}
            checked={confirmed}
            aria-invalid={invalid || undefined}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          {invalid && (
            <ValidationMessage data-size="sm">
              {t('safetyRep.signing.confirmError')}
            </ValidationMessage>
          )}
          <div className={layout.actions} data-gap="sm">
            <Button
              variant="primary"
              data-size="sm"
              onClick={confirm}
              loading={sign.isPending}
            >
              {t('safetyRep.work.documentation.sign')}
            </Button>
            <Button
              variant="secondary"
              data-size="sm"
              onClick={() => start(null)}
            >
              {t('safetyRep.signing.cancel')}
            </Button>
          </div>
        </div>
      )}
      {decision === 'return' && (
        <div className={classes.decision}>
          <Field data-size="sm">
            <Label htmlFor={`${id}-comment`}>
              {t('safetyRep.signing.missing')}
            </Label>
            <Field.Description id={`${id}-comment-description`}>
              {t('safetyRep.work.documentation.missingDescription')}
            </Field.Description>
            <Textarea
              id={`${id}-comment`}
              rows={3}
              aria-describedby={`${id}-comment-description`}
              aria-invalid={invalid || undefined}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
            {invalid && (
              <ValidationMessage>
                {t('safetyRep.signing.missingError')}
              </ValidationMessage>
            )}
          </Field>
          <div className={layout.actions} data-gap="sm">
            <Button
              variant="primary"
              data-size="sm"
              onClick={confirm}
              loading={giveBack.isPending}
            >
              {t('safetyRep.signing.sendBack')}
            </Button>
            <Button
              variant="secondary"
              data-size="sm"
              onClick={() => start(null)}
            >
              {t('safetyRep.signing.cancel')}
            </Button>
          </div>
        </div>
      )}
      {(sign.isError || giveBack.isError) && (
        <Alert data-color="danger">
          <Paragraph data-size="sm">
            {t('safetyRep.work.documentation.failed')}
          </Paragraph>
        </Alert>
      )}
    </div>
  );
};
